import { createContext, useEffect, useState } from "react";
import {
  login,
  logout,
  deactivateAccount,
  updateLoginUserProfile,
  updateLoginUserHalfProfile,
  updateLoggedInUserProfile,
  signup,
  resendEmailVerification,
  verifyEmail,
  changeUserPassword,
  forgotUserPassword,
  resetUserPassword,
  supportServiceMail,
  createUserShortcut,
  checkUserExists,
  checkUserEmailVerificationStatus,
  getUserProfile,
} from "Services/userService";
import * as LocalStorageService from "../utils/localStorageService";
import {
  shopifyUpdateDiscount,
  updateShopifyShopDetail,
  updateThreshold,
  updateUserShopping,
  updateEmailDeliveryDays
} from "Services/externalApiService";
import { uploadFile } from "Services/uploadService";
import {
  updateStoreDemoRescheduleUrl,
  getStoreHostRewardType,
  updateStoreHostRewardType,
} from "Services/storeService";
import logger from "utils/logger";
import { mapBackendStepsToFrontend, STORE_ONBOARDING_STEP_CONFIG } from "utils/onboardingStepMapper";

const AuthContext = createContext();

export default AuthContext;

export const AuthProvider = ({ children }) => {
  const [authTokens, setAuthTokens] = useState(() =>
    localStorage.getItem("authTokens")
      ? JSON.parse(localStorage.getItem("authTokens"))
      : null
  );

  const [user, setUser] = useState(() =>
    localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : null
  );
  const [loggedIn, setLoggedIn] = useState(() =>
    localStorage.getItem("authTokens") ? true : false
  );
  const [userProfile, setUserProfile] = useState(() =>
    localStorage.getItem("userProfile")
      ? JSON.parse(localStorage.getItem("userProfile"))
      : null
  );

  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [emailVerificationLoading, setEmailVerificationLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [isAccountConnected, setIsAccountConnected] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(() => 
    localStorage.getItem("onboardingStep") ? parseInt(localStorage.getItem("onboardingStep")) : 0
  );
  const [onboardingStepCompletion, setOnboardingStepCompletion] = useState(() =>
    localStorage.getItem("onboardingStepCompletion")
      ? JSON.parse(localStorage.getItem("onboardingStepCompletion"))
      : {}
  );
  //errors
  const [loginErrors, setLoginErrors] = useState(null);
  const [signupErrors, setSignupErrors] = useState(null);
  const [error, setError] = useState(null);

  let loginUser = async (e) => {
    let body = {
      email: e.email,
      password: e.password,
    };

    try {
      const response = await login(body);
      let userData = {
        firstName: response?.data?.data?.first_name,
        lastName: response.data.data.last_name,
        email: response.data.data.email,
        uid: response.data.data.uid,
        role: response.data.data.role,
        // Seed for CapabilityContext; confirmed against /me/capabilities/ on load.
        capabilities: response.data.data.capabilities || [],
        isVerified: response.data.data.is_verified,
        hasPasswordExpired: response.data.data.has_password_expired,
        isFirstTime: response.data.data.is_soft_host,
        completed_onboarding: response.data.data.completed_onboarding,
        payment_method: response.data.data.payment_method,
      };
      let tokens = {
        access: response.data.data.access,
        refresh: response.data.data.refresh,
      };
      setAuthTokens(tokens);
      setLoggedIn(true);
      setUser(userData);
      localStorage.setItem("authTokens", JSON.stringify(tokens));
      localStorage.setItem("user", JSON.stringify(userData));
      return response;
    } catch (error) {
      setLoginErrors(error.response.data);
      throw error;
    }
  };

  let signUpUser = async (data) => {
    try {
      const response = await signup(data);
      return response;
    } catch (error) {
      if (error.response.data.email) {
        setSignupErrors(error?.response?.data?.email[0]);
      } else if (error.response.data.confirm_password) {
        setSignupErrors(error?.response?.data?.confirm_password[0]);
      } else if (error.response.data.password) {
        setSignupErrors(error?.response?.data?.password[0]);
      } else if (error.response.data.shopify) {
        setSignupErrors(error?.response?.data?.shopify[0]);
      } else if (error.response.data.role) {
        setSignupErrors(error?.response?.data?.role[0]);
      } else {
        setSignupErrors("Error signing up!");
      }
      throw error;
    }
  };

  let shopifyThreshold = async (data) => {
    try {
      const response = await updateThreshold(data);
      return response;
    } catch (error) {
      throw error;
    }
  };
  let logoutUser = async (e) => {
    let body = {
      refresh: LocalStorageService.getRefreshToken(),
    };
    try {
      const response = await logout(body);
      if (response.status === 200) {
        setAuthTokens(null);
        setUser(null);
        setLoggedIn(false);
        localStorage.clear();
        return response;
      }
    } catch (error) {
      // alert(error.response.data.message);
      throw error;
    }
  };

  let deactivateUserAccount = async () => {
    try {
      const response = await deactivateAccount();
      if (response.status === 200) {
        setAuthTokens(null);
        setUser(null);
        setLoggedIn(false);
        localStorage.clear();
        return response;
      }
    } catch (error) {
      throw error;
    }
  };

  let updateProfile = async (e) => {
    setProfileLoading(true);
    try {
      const response = await updateLoggedInUserProfile();
      if (response?.data?.data[0]) {
        if (response?.data?.data.length > 0 && response?.data?.data[0].is_verified) {
          setIsEmailVerified(true);
        } else if (response?.data?.data.length > 0 && !response?.data?.data[0].is_verified) {
          setIsEmailVerified(false);
        }
        const userProfile = e === "halfProfile" ? { ...response.data.data[0], half: true } : response.data.data[0];
        setUserProfile(userProfile);
        localStorage.setItem("userProfile", JSON.stringify(userProfile));

        // Handle onboarding step tracking (backward compatibility)
        if (userProfile?.onboarding_step !== undefined) {
          setOnboardingStep(userProfile.onboarding_step);
          localStorage.setItem("onboardingStep", userProfile.onboarding_step.toString());
        }

        // Handle NEW API response fields for non-linear onboarding
        // Backend returns completedSteps at root level or onboarding_completed_steps in data
        const completedStepsArray =
          response?.data?.completedSteps ||
          response?.data?.onboarding_completed_steps ||
          userProfile?.onboarding_completed_steps ||
          [];

        if (completedStepsArray.length > 0) {
          const completedStepsObject = mapBackendStepsToFrontend(
            completedStepsArray,
            STORE_ONBOARDING_STEP_CONFIG
          );

          setOnboardingStepCompletion(completedStepsObject);
          localStorage.setItem(
            "onboardingStepCompletion",
            JSON.stringify(completedStepsObject)
          );
        } else if (userProfile?.onboarding_step_completion) {
          // Fallback to old format if new fields not present
          setOnboardingStepCompletion(userProfile.onboarding_step_completion);
          localStorage.setItem(
            "onboardingStepCompletion",
            JSON.stringify(userProfile.onboarding_step_completion)
          );
        }

        return response;
      }
    } catch (error) {
      throw error;
    } finally {
      setProfileLoading(false);
    }
  };

  let getProfileUser = async () => {
    try {
      const response = await updateLoggedInUserProfile();
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getHalfProfileUser = async (id) => {
    try {
      const response = await getUserProfile(id);
      return response;
    } catch (error) {
      throw error;
    }
  };

  let updateUserProfile = async (data) => {
    var body = {
      phone_number: data.phone_number,
      about_me: data.about_me,
      social: {
        instagram_handle: data.instagram_handle,
        twitter_handle: data.twitter_handle,
        tiktok_handle: data.tiktok_handle,
        facebook_handle: data.facebook_handle,
      },
      location: {
        latitude: data.latitude,
        longitude: data.longitude,
        address: data.address,
        street_number: data.street_number,
        city: data.city,
        state: data.state,
        zip_code: data.zip_code,
      },
      profile_picture_id: data.profile_picture_id,
      accounts: data.accounts,
      store: data.store
    };
    try {
      const response = await updateLoginUserProfile(body, user.uid);
      if (response.status === 200) {
        updateProfile();
      }
      //should manage to redirect to /user/showroom
      return response;
    } catch (error) {
      console.log("Error on profile form", error);
      // throw error;
    }
  };
  /**
   * Builds a partial payload for half-profile PUT.
   * Only includes keys that are present and non-empty (per backend contract).
   */
  const buildHalfProfilePartialBody = (data) => {
    const body = {};
    const str = (v) => v != null && String(v).trim() !== "";
    if (str(data.phone_number)) body.phone_number = data.phone_number;
    if (str(data.about_me)) body.about_me = data.about_me;
    if (data.profile_picture_id != null && data.profile_picture_id !== "") body.profile_picture_id = data.profile_picture_id;
    const social = {};
    if (str(data.instagram_handle)) social.instagram_handle = data.instagram_handle;
    if (str(data.twitter_handle)) social.twitter_handle = data.twitter_handle;
    if (str(data.tiktok_handle)) social.tiktok_handle = data.tiktok_handle;
    if (str(data.facebook_handle)) social.facebook_handle = data.facebook_handle;
    if (Object.keys(social).length > 0) body.social = social;
    const location = {};
    if (data.latitude != null && data.latitude !== "") location.latitude = data.latitude;
    if (data.longitude != null && data.longitude !== "") location.longitude = data.longitude;
    if (str(data.address)) location.address = data.address;
    if (str(data.street_number)) location.street_number = data.street_number;
    if (str(data.city)) location.city = data.city;
    if (str(data.state)) location.state = data.state;
    if (str(data.zip_code)) location.zip_code = String(data.zip_code).trim().slice(0, 5);
    if (Object.keys(location).length > 0) body.location = location;
    if (Array.isArray(data.accounts)) body.accounts = data.accounts;
    if (data.store != null && typeof data.store === "object" && Object.keys(data.store).length > 0) body.store = data.store;
    if (Array.isArray(data.onboarding_completed_steps)) body.onboarding_completed_steps = data.onboarding_completed_steps;
    if (typeof data.agreement_accepted === "boolean") body.agreement_accepted = data.agreement_accepted;
    return body;
  };

  const updateUserHalfProfile = async (data) => {
    const body = buildHalfProfilePartialBody(data);
    try {
      const response = await updateLoginUserHalfProfile(body, user.uid);
      if (response.status === 200) {
        updateProfile("halfProfile");
      }
      return response;
    } catch (error) {
      console.log("Error on profile form", error);
      throw error;
    }
  };

  let updateMultipleEmails = async (data) => {
    try {
      const res = await updateLoginUserProfile(data, user.uid);
      if (res.status === 200) {
        updateProfile();
      }
      return res;
    } catch (error) {
      throw error;
    }
  };

  let updateStoreUserProfile = async (data) => {
    var body = {
      phone_number: data.phone_number,
      about_me: data.about_me,
      social: {
        instagram_handle: data.instagram_handle,
        twitter_handle: data.twitter_handle,
        tiktok_handle: data.tiktok_handle,
        facebook_handle: data.facebook_handle,
      },
      location: {
        latitude: data.latitude,
        longitude: data.longitude,
        address: data.address,
        street_number: data.street_number,
        city: data.city,
        state: data.state,
        zip_code: data.zip_code,
      },
      profile_picture_id: data.profile_picture_id,
      accounts: [
        {
          store_email: data.store_email,
        },
      ],
      store: {
        host_reward_type: data.rewardType ?? userProfile?.store?.host_reward_type,
        demo_commission: data.demoCommission,
        demo_commission_type: data.commissionType,
        demo_sales_commission: data.salesCommission,
        demo_sales_commission_type: data.salesCommissionType,
        discount_type: data.discountType,
        discount_given: data.discountGiven,
        payment_frequency: data.paymentFrequency,
        no_show_demo_commission_type: data.no_show_demo_commission_type,
        no_show_demo_commission: data.no_show_demo_commission,
        sales_commission_order_threshold: data?.timeEligibility,
        discount_config: data.discount_config || data.discountConfig,
        is_guest_discount_active: data.is_guest_discount_active ?? userProfile?.store?.is_guest_discount_active
      },
    };
    try {
      const response = await updateLoginUserProfile(body, user.uid);
      if (response.status === 200) {
        const dataShopify = {
          storeid: userProfile?.store?.store_id,
          discount_type: data.discountType,
          discount_given: parseFloat(data.discountGiven),
        };
        try {
          const res = await shopifyUpdateDiscount(dataShopify);
          if (res) {
            if (data?.files) {
              const bodyShopify = {
                logo: data?.files,
                videoid: data?.videofile,
                days: data?.shipDate,
                nameShop: data?.shopName,
                storeId: userProfile?.store?.store_id,
              };
              try {
                await updateShopifyShopDetail(bodyShopify);
                await updateEmailDeliveryDays(
                  userProfile?.store?.id,
                  data?.shipDate
                );
              } catch (error) {
                console.log("Error Occured");
              }
            }
          }
        } catch (error) {
          console.log("Error Occured");
        }

        updateProfile();
      }
      //should manage to redirect to /user/showroom
      return response;
    } catch (error) {
      console.log("Error on profile form");
      throw error;
    }
  };

  const getStoreRewardType = async () => {
    try {
      const response = await getStoreHostRewardType();
      return response;
    } catch (error) {
      logger.error("Error fetching store reward type:", error);
      throw error;
    }
  };

  const updateStoreRewardType = async (hostRewardType) => {
    try {
      // API expects: { "host_reward_type": "store_credit" } or { "host_reward_type": "cash" }
      // hostRewardType should be "cash" or "store_credit" (lowercase with underscore)
      const response = await updateStoreHostRewardType({
        host_reward_type: hostRewardType,
      });
      if (response?.status === 200) {
        await updateProfile();
      }
      return response;
    } catch (error) {
      logger.error("Error updating store reward type:", error);
      throw error;
    }
  };

  let updateShopifyStoreDetail = async (data) => {
    try {
      const bodyShopify = {
        logo: data.logo,
        days: data.delivery,
        nameShop: data.name,
        storeId: userProfile.store.store_id,
        videoid: data.videoid,
      };
      try {
        const res = await updateShopifyShopDetail(bodyShopify);

        // 2️⃣ Update email delivery days
        if (data?.delivery) {
          await updateEmailDeliveryDays(
            userProfile.store.id,
            data.delivery
          );
        }
        return res;
      } catch (error) {
        console.log("Error Occured");
      }
    } catch (error) {
      console.log("Error Occured");
    }
  };

  const updateStoreUrl = async (data, params) => {
    try {
      const res = await updateStoreDemoRescheduleUrl( params, data);
      return res;
    }
    catch (error) {
      console.log("Error Occured", error);
    }
  };

  //here this will help to upload shopify store logo
  let uploader = async (values) => {
    let body = {
      tag: "Store_logo",
      type: values.files[0].type,
      extension: "jpeg",
      file: values.files[0],
    };
    try {
      const response = await uploadFile(body);
      values.logo = response.data.data.id;
      if (response.status === 200) {
        const updateShopifyStoreLogo = await updateShopifyStoreDetail(values);
        return updateShopifyStoreLogo;
      } else {
        return;
      }
    } catch (error) {
      throw error;
    }
  };

  //here this will help to upload shopify store video
  let storeVideoUploader = async (values) => {
    let body = {
      tag: "Store_video",
      type: values.video.type,
      extension: "mp4",
      file: values.video,
    };
    try {
      const response = await uploadFile(body);
      let payload = {
        storeId: values?.storeid,
        logo: values?.logo,
        nameShop: values?.nameShop,
        days: values?.days,
        videoid: response?.data?.data?.id,
      };
      if (response.status === 200) {
        await updateShopifyShopDetail(payload);
        return response;
      } else {
        return;
      }
    } catch (error) {
      // throw error;
      console.log("Error Occured");
    }
  };

  //Password related stuffs
  let resendVerification = async () => {
    try {
      const response = await resendEmailVerification();
      return response;
    } catch (error) {
      throw error;
    }
  };

  let verificationEmail = async (data, params) => {
    try {
      const response = await verifyEmail(data, params);
      return response;
    } catch (error) {
      throw error;
    }
  };
  let changePasswords = async (value) => {
    const body = {
      old_password: value.old_password,
      password: value.password,
      confirm_password: value.confirm_password,
      type: value.type,
    };
    try {
      const response = await changeUserPassword(body);
      return response;
    } catch (error) {
      setError(error.response.data);
      throw error;
    }
  };
  let forgotPassword = async (value) => {
    const body = {
      email: value.email,
    };
    try {
      const response = await forgotUserPassword(body);
      return response;
    } catch (error) {
      throw error;
    }
  };
  let resetPassword = async (value) => {
    const userId = value.uid;
    const userToken = value.userToken;
    const body = {
      new_password: value.password,
      confirm_password: value.confirm_password,
    };
    try {
      const response = await resetUserPassword(body, userId, userToken);
      return response;
    } catch (error) {
      throw error;
    }
  };
  let sendSupportMail = async (body) => {
    try {
      const res = await supportServiceMail(body);
      return res;
    } catch (error) {
      throw error;
    }
  };

  let updateShopifySignup = async (params) => {
    try {
      await updateUserShopping(params);
    } catch (error) {
      throw error;
    }
  };

  let userCreateShort = async (body, params) => {
    try {
      let res = await createUserShortcut(body, params);
      return res;
    } catch (error) {
      throw error;
    }
  };
  let userExistCheck = async (body) => {
    try {
      let res = await checkUserExists(body);
      return res;
    } catch (error) {
      throw error;
    }
  };
  // useEffect(() => {
  //   if (loggedIn) {
  //     updateProfile();
  //   }
  // }, [loggedIn]);

  /**
   * This function is used to make a api call that checks the email verification of the logged-in user.
   * Every role is checked: invited teammates (owner, edit admin, accountant, ...) are verified when
   * they complete onboarding, and without this call they would see the "not verified" banner forever.
   *
   * @returns {void}
   */

  const getEmailVerificationStatus = async () => {

    if (user) {
      if (loggedIn) {
        try {
          setEmailVerificationLoading(true);
          const response = await checkUserEmailVerificationStatus();
          if (response?.data?.data?.is_verified) {
            setIsEmailVerified(true);
          } else {
            setIsEmailVerified(false);
          }
          setEmailVerificationLoading(false);
        } catch (error) {
          setIsEmailVerified(false);
          setEmailVerificationLoading(false);
        }
      }
    }
  }

  useEffect(()=> {
    getEmailVerificationStatus()
  },[])

  let contextData = {
    user: user,
    setUser: setUser,
    authTokens: authTokens,
    setAuthTokens: setAuthTokens,
    userProfile: userProfile,
    loginUser: loginUser,
    updateProfile: updateProfile,
    updateUserProfile: updateUserProfile,
    getProfileUser: getProfileUser,
    logoutUser: logoutUser,
    deactivateUserAccount: deactivateUserAccount,
    forgotPassword: forgotPassword,
    resetPassword: resetPassword,
    changePasswords: changePasswords,
    resendVerification: resendVerification,
    loginErrors: loginErrors,
    loggedIn: loggedIn,
    setLoggedIn: setLoggedIn,
    signUpUser: signUpUser,
    signupErrors: signupErrors,
    setSignupErrors: setSignupErrors,
    shopifyThreshold: shopifyThreshold,
    verificationEmail: verificationEmail,
    error: error,
    sendSupportMail: sendSupportMail,
    updateStoreUserProfile: updateStoreUserProfile,
    updateMultipleEmails: updateMultipleEmails,
    updateShopifySignup: updateShopifySignup,
    userCreateShort: userCreateShort,
    userExistCheck: userExistCheck,
    updateShopifyStoreDetail: updateShopifyStoreDetail,
    // putToS3: putToS3,
    uploader: uploader,
    storeVideoUploader: storeVideoUploader,
    updateUserHalfProfile: updateUserHalfProfile,
    isEmailVerified,
    emailVerificationLoading,
    profileLoading,
    setIsEmailVerified,
    getHalfProfileUser,
    updateStoreUrl,
    getStoreRewardType,
    updateStoreRewardType,
    onboardingStep,
    setOnboardingStep,
    onboardingStepCompletion,
    setOnboardingStepCompletion,
  };

  return (
    <AuthContext.Provider value={contextData}>{children}</AuthContext.Provider>
  );
};
